import { useState } from "react";
import { toast } from "sonner";
import { Heart, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export const EmailCapture = ({
  source,
  heading,
  subheading,
  buttonLabel = "Count Me In",
  dark = false,
  testidPrefix,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/signups", { name, email, source });
      setDone(true);
      toast.success("Welcome to the community — we'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = dark
    ? "w-full rounded-full border border-ivory/30 bg-ivory/10 px-5 py-3.5 text-sm text-ivory placeholder:text-ivory/60 outline-none transition-colors focus:border-gold-bright"
    : "w-full rounded-full border border-line bg-white px-5 py-3.5 text-sm text-ink placeholder:text-taupe outline-none transition-colors focus:border-gold";

  return (
    <div data-testid={`${testidPrefix}-capture`}>
      <h2
        className={`font-serif text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl ${
          dark ? "text-ivory" : "text-ink"
        }`}
      >
        {heading}
      </h2>
      {subheading && (
        <p
          className={`mt-3 max-w-xl text-base leading-relaxed ${
            dark ? "text-ivory/80" : "text-cocoa"
          }`}
        >
          {subheading}
        </p>
      )}

      {done ? (
        <div
          className={`mt-8 flex items-center gap-3 rounded-2xl border px-6 py-5 ${
            dark ? "border-gold-bright/40 bg-ivory/10" : "border-gold/40 bg-gold-soft"
          }`}
          data-testid={`${testidPrefix}-success`}
        >
          <Heart className="h-6 w-6 shrink-0 text-gold" fill="currentColor" />
          <p className={`font-serif text-lg italic ${dark ? "text-ivory" : "text-ink"}`}>
            You're in the Circle! We'll write soon.
          </p>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="mt-8 flex max-w-xl flex-col gap-3 sm:flex-row sm:flex-wrap"
          data-testid={`${testidPrefix}-form`}
        >
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            aria-label="Your name"
            className={`${inputCls} sm:flex-1`}
            data-testid={`${testidPrefix}-name-input`}
          />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            aria-label="Your email"
            className={`${inputCls} sm:flex-1`}
            data-testid={`${testidPrefix}-email-input`}
          />
          <button
            type="submit"
            disabled={loading}
            className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-colors duration-200 disabled:opacity-60 ${
              dark
                ? "bg-gold text-ink hover:bg-gold-bright"
                : "bg-crimson text-ivory hover:bg-crimson-deep"
            }`}
            data-testid={`${testidPrefix}-submit-button`}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {buttonLabel}
          </button>
        </form>
      )}
    </div>
  );
};
